// AppBarV2 — 48px top bar with logo + product name + breadcrumb + (mock)
// global search + right-aligned slot for app-specific bits like the user
// profile menu.
//
// Search is decorative for now (renders a non-functional input that opens
// a callback so the host app can show a placeholder modal later).

import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { SearchIcon } from './icons';

export const APPBAR_V2_HEIGHT = 48;

export interface AppBarV2Props {
    /** Logo / brand mark on the far left. */
    logo?: React.ReactNode;
    /** Product or app name shown next to the logo. */
    productName?: string;
    /** Optional breadcrumb / context node rendered between brand and search. */
    breadcrumb?: React.ReactNode;
    /** Right-aligned slot, typically the user-profile menu. */
    rightAccessory?: React.ReactNode;
    /** Called when the user clicks the (decorative) search box. */
    onSearchClick?: () => void;
    /** Override the search box placeholder text. */
    searchPlaceholder?: string;
    /** Hide the search box entirely. */
    hideSearch?: boolean;
    /**
     * Replace the built-in decorative search with a host-provided
     * node (e.g. <DashboardsAutocompleteV2 />). When provided,
     * `onSearchClick` and `searchPlaceholder` are ignored.
     */
    searchSlot?: React.ReactNode;
    /**
     * Hamburger trigger rendered before the logo. Hosts mount this on
     * the responsive (< lg) breakpoint to open the MobileDrawerV2.
     */
    hamburger?: React.ReactNode;
    /**
     * Compact mode — the responsive (< lg) chrome. Hides the search
     * box (no room next to the hamburger), tightens horizontal gaps,
     * and lets the breadcrumb slot carry just the company chip (the
     * host decides what to pass). The desktop layout is preserved
     * when this is `false` (the default).
     */
    compact?: boolean;
}

export const AppBarV2: React.FC<AppBarV2Props> = ({
    logo,
    productName = 'Agentic Explorer',
    breadcrumb,
    rightAccessory,
    onSearchClick,
    searchPlaceholder = 'Buscar dashboards, métricas\u2026',
    hideSearch = false,
    searchSlot,
    hamburger,
    compact = false,
}) => {
    const searchHidden = hideSearch || compact;
    return (
        <Box
            component="header"
            sx={(theme) => ({
                height: APPBAR_V2_HEIGHT,
                flexShrink: 0,
                backgroundColor: theme.palette.background.paper,
                borderBottom: `1px solid ${theme.palette.divider}`,
                display: 'flex',
                alignItems: 'center',
                px: compact ? 1 : 2,
                gap: compact ? 1 : 1.5,
                zIndex: 1,
            })}
        >
            {hamburger && (
                <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                    {hamburger}
                </Box>
            )}
            {logo && <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>{logo}</Box>}
            {productName && (
                <Typography
                    sx={(theme) => ({
                        fontSize: 13,
                        fontWeight: 500,
                        color: theme.palette.text.primary,
                        flexShrink: 0,
                        whiteSpace: 'nowrap',
                    })}
                >
                    {productName}
                </Typography>
            )}

            {breadcrumb && (
                <>
                    <Box sx={(theme) => ({ width: '1px', height: 18, backgroundColor: theme.palette.divider })} />
                    <Box sx={(theme) => ({ display: 'flex', alignItems: 'center', gap: 0.75, fontSize: 12, color: theme.palette.text.secondary, minWidth: 0, overflow: 'hidden' })}>
                        {breadcrumb}
                    </Box>
                </>
            )}

            <Box sx={{ flex: 1 }} />

            {!searchHidden && (
                searchSlot ?? (
                    <Box
                        role="button"
                        tabIndex={0}
                        onClick={onSearchClick}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onSearchClick?.(); }}
                        sx={(theme) => ({
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            backgroundColor: theme.palette.surface.raised,
                            border: `1px solid ${theme.palette.divider}`,
                            borderRadius: `${theme.shape.radiusSm}px`,
                            px: 1.25,
                            py: 0.5,
                            minWidth: 240,
                            cursor: 'pointer',
                            color: theme.palette.text.disabled,
                            transition: 'background-color .12s, color .12s',
                            '&:hover': { backgroundColor: theme.palette.surface.hover, color: theme.palette.text.secondary },
                        })}
                    >
                        <SearchIcon />
                        <Typography sx={{ fontSize: 12, color: 'inherit', flex: 1 }}>{searchPlaceholder}</Typography>
                        <Box
                            sx={(theme) => ({
                                fontFamily: theme.typography.fontFamilyMono,
                                fontSize: 10,
                                color: theme.palette.text.disabled,
                                border: `1px solid ${theme.palette.divider}`,
                                borderRadius: 0.5,
                                px: 0.6,
                                py: 0.1,
                            })}
                        >
                            ⌘K
                        </Box>
                    </Box>
                )
            )}

            {rightAccessory}
        </Box>
    );
};

AppBarV2.displayName = 'AppBarV2';
